import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardData } from '../hooks/useDashboardData';
import ProjectIcon from './icons/ProjectIcon';
import WorkspaceIcon from './icons/WorkspaceIcon';
import StarIcon from './icons/StarIcon';
import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalTitle,
    CloseButton,
    ModalBody,
    SearchInput,
    SectionTitle,
    SectionContainer,
    ProjectItem,
    ProjectItemTitle,
    ActiveIndicator,
    EmptyState,
} from '../styles/SwitchBoardModal.styles';

const SwitchBoardModal = ({ isOpen, onClose, currentProjectId }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const {
        projects,
        starredProjectIds,
        workspaces,
        projectsByWorkspace,
        unassignedProjects,
    } = useDashboardData(user);

    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
        if (!isOpen) {
            setSearchQuery('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleProjectClick = (projectId) => {
        if (projectId !== currentProjectId) {
            navigate(`/project/${projectId}`);
        }
        onClose();
    };

    const starredProjects = projects.filter(p => starredProjectIds.includes(p.id));

    const filterProjects = (projectList) => {
        if (!searchQuery.trim()) return projectList;
        const query = searchQuery.toLowerCase();
        return projectList.filter(p =>
            p.title?.toLowerCase().includes(query)
        );
    };

    const filteredStarred = filterProjects(starredProjects);
    const filteredUnassigned = filterProjects(unassignedProjects);

    const hasAnyResults = () => {
        if (filteredStarred.length > 0 || filteredUnassigned.length > 0) return true;
        for (const workspace of workspaces) {
            const wsProjects = projectsByWorkspace[workspace.id] || [];
            if (filterProjects(wsProjects).length > 0) return true;
        }
        return false;
    };

    const renderProjectItem = (project) => (
        <ProjectItem
            key={project.id}
            isActive={project.id === currentProjectId}
            onClick={() => handleProjectClick(project.id)}
            data-testid={`switch-board-project-${project.id}`}
        >
            <ProjectIcon />
            <ProjectItemTitle>{project.title}</ProjectItemTitle>
            {project.id === currentProjectId && (
                <ActiveIndicator>Current</ActiveIndicator>
            )}
        </ProjectItem>
    );

    return (
        <ModalOverlay onClick={handleOverlayClick} data-testid="switch-board-modal-overlay">
            <ModalContent>
                <ModalHeader>
                    <ModalTitle>Switch board</ModalTitle>
                    <CloseButton onClick={onClose} data-testid="switch-board-close">×</CloseButton>
                </ModalHeader>
                <ModalBody>
                    <SearchInput
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search projects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="switch-board-search"
                    />

                    {!hasAnyResults() && (
                        <EmptyState>
                            {searchQuery ? 'No projects found' : 'No projects available'}
                        </EmptyState>
                    )}

                    {filteredStarred.length > 0 && (
                        <SectionContainer>
                            <SectionTitle>
                                <StarIcon />
                                Starred
                            </SectionTitle>
                            {filteredStarred.map(renderProjectItem)}
                        </SectionContainer>
                    )}

                    {workspaces.map(workspace => {
                        const wsProjects = filterProjects(projectsByWorkspace[workspace.id] || []);
                        if (wsProjects.length === 0) return null;
                        return (
                            <SectionContainer key={workspace.id}>
                                <SectionTitle>
                                    <WorkspaceIcon />
                                    {workspace.name}
                                </SectionTitle>
                                {wsProjects.map(renderProjectItem)}
                            </SectionContainer>
                        );
                    })}

                    {filteredUnassigned.length > 0 && (
                        <SectionContainer>
                            <SectionTitle>
                                <ProjectIcon />
                                Other Projects
                            </SectionTitle>
                            {filteredUnassigned.map(renderProjectItem)}
                        </SectionContainer>
                    )}
                </ModalBody>
            </ModalContent>
        </ModalOverlay>
    );
};

export default SwitchBoardModal;
